import { Check, ChevronDown } from 'lucide-react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

import styles from './AllocationAggregationMenu.module.scss'

// Aggregation options surfaced both on the cliente detail page allocation
// card and inside the ClientBriefArtifact composition section. Today only
// `asset_class` is wired to a real breakdown; the other entries advertise
// upcoming feeds and stay disabled with the "Disponibile a breve" hint.
export type AllocationAggregationId =
  | 'asset_class'
  | 'asset_type'
  | 'country'
  | 'currency'
  | 'instrument_type'

export const ALLOCATION_AGGREGATION_OPTIONS: ReadonlyArray<{
  id: AllocationAggregationId
  label: string
  hint?: string
  disabled?: boolean
}> = [
  { id: 'asset_class', label: 'Asset class', hint: 'Aggregazione corrente' },
  { id: 'asset_type', label: 'Asset type', hint: 'Disponibile a breve', disabled: true },
  { id: 'country', label: 'Paese', hint: 'Disponibile a breve', disabled: true },
  { id: 'currency', label: 'Esposizione valutaria', hint: 'Disponibile a breve', disabled: true },
  {
    id: 'instrument_type',
    label: 'Tipo strumento/servizio',
    hint: 'Disponibile a breve',
    disabled: true,
  },
]

export function allocationAggregationLabel(id: AllocationAggregationId): string {
  return (
    ALLOCATION_AGGREGATION_OPTIONS.find((option) => option.id === id)?.label ?? 'Asset class'
  )
}

// Mirrors the SegmentDropdown pattern from ClientsListSection: a single
// rounded chip trigger with a chevron that opens a Popover listbox. Sector
// and geography aggregations are present as menu items but disabled until
// the breakdown feeds are wired.
export function AllocationAggregationMenu({
  aggregation,
  onChange,
}: {
  aggregation: AllocationAggregationId
  onChange: (next: AllocationAggregationId) => void
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={styles.trigger}
          aria-label={`Aggregazione allocazione: ${allocationAggregationLabel(aggregation)}`}
        >
          <span className={styles.triggerLabel}>
            {allocationAggregationLabel(aggregation)}
          </span>
          <ChevronDown className={styles.triggerChevron} aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="end" sideOffset={8} className={styles.menu}>
        <p className={styles.menuLabel}>Aggrega per</p>
        <ul
          role="listbox"
          aria-label="Aggregazione allocazione"
          className={styles.menuList}
        >
          {ALLOCATION_AGGREGATION_OPTIONS.map((option) => {
            const selected = option.id === aggregation
            return (
              <li key={option.id} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  aria-disabled={option.disabled || undefined}
                  disabled={option.disabled}
                  className={cn(
                    styles.menuItem,
                    selected && styles.menuItemSelected,
                    option.disabled && styles.menuItemDisabled,
                  )}
                  onClick={() => {
                    if (!option.disabled) onChange(option.id)
                  }}
                >
                  <span className={styles.menuItemBody}>
                    <span className={styles.menuItemTitle}>{option.label}</span>
                    {option.hint ? (
                      <span className={styles.menuItemHint}>{option.hint}</span>
                    ) : null}
                  </span>
                  {selected ? (
                    <Check className={styles.menuItemCheck} aria-hidden />
                  ) : null}
                </button>
              </li>
            )
          })}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
